// components/AdminMessageToast.js
// In-app toast for admins when players send chat messages.
//
//  • On opening the website: if there are unread player messages, shows ONE
//    toast — the message itself if it's a single conversation, or a summary
//    ("3 unread messages from 2 players") if there are several.
//  • While the site is open: every new player message pops a toast with the
//    sender, event and message preview, live via onSnapshot.
//  • "Reply" opens that exact conversation (/my-events?chat=<id>, handled in
//    MyEvents.js). Toasts auto-dismiss and disappear if the chat gets read.
//  • Browser tab title shows the unread count, e.g. "(3) Panda Badminton…".
//
// Data source: conversations where unreadByAdmin > 0 (the same counter
// ChatModal.js increments when a player sends). Single-field inequality, so
// no composite index is needed.
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';

const LIVE_TOAST_MS = 10000;
const SUMMARY_TOAST_MS = 14000;
const MAX_TOASTS = 3;
const SESSION_KEY = 'adminMsgToastShown';

const tsMillis = (v) => (v && typeof v.toMillis === 'function' ? v.toMillis() : 0);

function AdminMessageToast() {
  const { currentUser, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [toasts, setToasts] = useState([]);
  const seenRef = useRef(new Map()); // convId -> lastMessageAt millis already handled
  const timersRef = useRef(new Map());
  const baseTitleRef = useRef(typeof document !== 'undefined' ? document.title : '');

  const dismiss = useCallback((key) => {
    setToasts((prev) => prev.filter((t) => t.key !== key));
    const tm = timersRef.current.get(key);
    if (tm) { clearTimeout(tm); timersRef.current.delete(key); }
  }, []);

  const push = useCallback((toast, ms) => {
    setToasts((prev) => {
      // One toast per conversation: a newer message replaces the older toast.
      const rest = prev.filter((t) => t.key !== toast.key && !(toast.convId && t.convId === toast.convId));
      return [toast, ...rest].slice(0, MAX_TOASTS);
    });
    const old = timersRef.current.get(toast.key);
    if (old) clearTimeout(old);
    timersRef.current.set(toast.key, setTimeout(() => dismiss(toast.key), ms));
  }, [dismiss]);

  useEffect(() => {
    if (!currentUser || !isAdmin) {
      setToasts([]);
      return undefined;
    }

    seenRef.current = new Map();
    let first = true;

    // Player → admin messages only. In an admin-to-admin DM, `adminId` is the
    // last admin who sent, so skip the ones I sent myself.
    const isRelevant = (c) =>
      !!c.lastMessage && !(c.isAdminToAdmin === true && c.adminId === currentUser.uid);

    const q = query(collection(db, 'conversations'), where('unreadByAdmin', '>', 0));

    const unsub = onSnapshot(q, (snap) => {
      const convs = snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter(isRelevant);

      // Tab title badge
      const total = convs.reduce((s, c) => s + (c.unreadByAdmin || 0), 0);
      const base = baseTitleRef.current.replace(/^\(\d+\)\s*/, '');
      document.title = total > 0 ? `(${total}) ${base}` : base;

      // Drop toasts for chats that have since been read
      const unreadIds = new Set(convs.map((c) => c.id));
      setToasts((prev) => prev.filter((t) => !t.convId || unreadIds.has(t.convId)));

      if (first) {
        first = false;
        convs.forEach((c) => seenRef.current.set(c.id, tsMillis(c.lastMessageAt)));
        if (convs.length === 0) return;

        // Don't repeat the identical "on open" toast after an in-session reload.
        const signature = convs.map((c) => `${c.id}:${tsMillis(c.lastMessageAt)}`).sort().join('|');
        try {
          if (sessionStorage.getItem(SESSION_KEY) === signature) return;
          sessionStorage.setItem(SESSION_KEY, signature);
        } catch (_) { /* private mode — just show it */ }

        const sorted = [...convs].sort((a, b) => tsMillis(b.lastMessageAt) - tsMillis(a.lastMessageAt));
        if (sorted.length === 1) {
          const c = sorted[0];
          push({ key: `conv-${c.id}`, convId: c.id, kind: 'message', conv: c }, SUMMARY_TOAST_MS);
        } else {
          push({ key: 'summary', kind: 'summary', convs: sorted, total }, SUMMARY_TOAST_MS);
        }
        return;
      }

      // Live: anything whose lastMessageAt moved forward is a new message
      snap.docChanges().forEach((change) => {
        if (change.type === 'removed') return;
        const c = { id: change.doc.id, ...change.doc.data() };
        if (!isRelevant(c)) return;
        const at = tsMillis(c.lastMessageAt);
        if (!at) return; // server timestamp not resolved yet — next snapshot has it
        const prevAt = seenRef.current.get(c.id) || 0;
        if (at <= prevAt) return; // e.g. lastAdminEmailAt / read-receipt updates
        seenRef.current.set(c.id, at);

        // Admin is already looking at this exact chat — no toast needed.
        if (window.__openChatConversationId === c.id) return;

        push({ key: `conv-${c.id}`, convId: c.id, kind: 'message', conv: c }, LIVE_TOAST_MS);
      });
    }, (err) => {
      console.error('[AdminMessageToast] listener error:', err);
    });

    const timers = timersRef.current;
    const baseTitle = baseTitleRef.current;
    return () => {
      unsub();
      timers.forEach((t) => clearTimeout(t));
      timers.clear();
      document.title = baseTitle.replace(/^\(\d+\)\s*/, '');
    };
  }, [currentUser, isAdmin, push]);

  if (!isAdmin || toasts.length === 0) return null;

  const openChat = (t) => {
    dismiss(t.key);
    navigate(t.convId ? `/my-events?chat=${encodeURIComponent(t.convId)}` : '/my-events');
  };

  return (
    <div className="amt-wrap">
      {toasts.map((t) => {
        if (t.kind === 'summary') {
          const names = [...new Set(t.convs.map((c) => c.userName || 'Player'))];
          const latest = t.convs[0];
          return (
            <div key={t.key} className="amt-card" role="status">
              <div className="amt-avatar amt-avatar-icon">
                <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" /></svg>
              </div>
              <div className="amt-body">
                <div className="amt-title">
                  {t.total} unread message{t.total === 1 ? '' : 's'} from {names.length} player{names.length === 1 ? '' : 's'}
                </div>
                <div className="amt-sub">
                  {names.slice(0, 3).join(', ')}{names.length > 3 ? ` +${names.length - 3}` : ''}
                </div>
                <div className="amt-preview">
                  <b>{latest.userName || 'Player'}:</b> {latest.lastMessage}
                </div>
                <div className="amt-actions">
                  <button className="amt-btn-primary" onClick={() => openChat(t)}>Open inbox →</button>
                  <button className="amt-btn-ghost" onClick={() => dismiss(t.key)}>Later</button>
                </div>
              </div>
              <button className="amt-close" aria-label="Dismiss" onClick={() => dismiss(t.key)}>×</button>
            </div>
          );
        }

        const c = t.conv;
        const name = c.userName || 'Player';
        const showEvent = c.eventTitle && c.eventTitle !== 'Direct Message' && c.eventTitle !== 'Admin Direct Message';
        return (
          <div key={t.key} className={`amt-card${c.isPaymentIssue ? ' amt-urgent' : ''}`} role="status">
            {c.userPhotoURL ? (
              <img className="amt-avatar" src={c.userPhotoURL} alt="" />
            ) : (
              <div className="amt-avatar">{name.charAt(0).toUpperCase()}</div>
            )}
            <div className="amt-body">
              <div className="amt-title">
                {name}
                {(c.unreadByAdmin || 0) > 1 && <span className="amt-badge">{c.unreadByAdmin}</span>}
                {c.isPaymentIssue && <span className="amt-tag">Payment issue</span>}
              </div>
              {showEvent && <div className="amt-sub">{c.eventTitle}</div>}
              <div className="amt-preview">{c.lastMessage}</div>
              <div className="amt-actions">
                <button className="amt-btn-primary" onClick={() => openChat(t)}>Reply →</button>
                <button className="amt-btn-ghost" onClick={() => dismiss(t.key)}>Later</button>
              </div>
            </div>
            <button className="amt-close" aria-label="Dismiss" onClick={() => dismiss(t.key)}>×</button>
          </div>
        );
      })}
      <style>{`
        .amt-wrap{position:fixed;top:12px;left:50%;transform:translateX(-50%);z-index:9997;width:92%;max-width:380px;display:flex;flex-direction:column;gap:8px;pointer-events:none}
        @media (min-width:768px){.amt-wrap{left:auto;right:16px;transform:none;top:16px}}
        .amt-card{pointer-events:auto;position:relative;display:flex;gap:12px;align-items:flex-start;background:#fff;border-radius:16px;padding:14px 34px 14px 14px;box-shadow:0 8px 32px rgba(0,0,0,.15);border:1px solid rgba(249,115,22,.25);animation:amtIn .35s ease-out}
        .amt-urgent{border-color:rgba(220,38,38,.45)}
        .amt-avatar{width:40px;height:40px;border-radius:50%;flex-shrink:0;object-fit:cover;background:linear-gradient(135deg,#fb923c,#f97316);color:#fff;font-weight:700;font-size:16px;display:flex;align-items:center;justify-content:center}
        .amt-avatar-icon{font-size:20px}
        .amt-body{flex:1;min-width:0}
        .amt-title{font-size:14px;font-weight:700;color:#111827;display:flex;align-items:center;gap:6px;flex-wrap:wrap}
        .amt-badge{background:#ef4444;color:#fff;font-size:11px;font-weight:700;border-radius:999px;padding:0 6px;line-height:18px}
        .amt-tag{background:#fee2e2;color:#b91c1c;font-size:10px;font-weight:700;border-radius:6px;padding:1px 6px}
        .amt-sub{font-size:12px;color:#6b7280;margin-top:2px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
        .amt-preview{font-size:13px;color:#374151;margin-top:6px;line-height:1.4;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;word-break:break-word}
        .amt-actions{display:flex;gap:8px;margin-top:10px}
        .amt-btn-primary{flex:1;padding:7px 0;border-radius:10px;border:none;background:linear-gradient(135deg,#fb923c,#f97316);color:#fff;font-size:12px;font-weight:700;cursor:pointer}
        .amt-btn-ghost{padding:7px 14px;border-radius:10px;border:none;background:#f3f4f6;color:#6b7280;font-size:12px;font-weight:600;cursor:pointer}
        .amt-close{position:absolute;top:8px;right:10px;background:none;border:none;color:#9ca3af;font-size:18px;line-height:1;cursor:pointer;padding:2px}
        @keyframes amtIn{from{opacity:0;transform:translateY(-16px) scale(.97)}to{opacity:1;transform:translateY(0) scale(1)}}
      `}</style>
    </div>
  );
}

export default AdminMessageToast;
